import apiFetch from "@/app/auth/apiFetch";
import type { NavigateFunction } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL;

// ---------------------------------------------------------------------------
// Types (le contrat backoffice est figé — cf. /admin/support/*).
// ---------------------------------------------------------------------------
export type TicketStatus = "open" | "pending" | "on_hold" | "solved" | "closed";
export type TicketPriority = "low" | "normal" | "high" | "urgent";
export type TicketCategory =
  | "question"
  | "billing"
  | "bug"
  | "feature_request"
  | "account"
  | "refund"
  | "other";
export type AuthorType = "customer" | "agent" | "system";
export type RefundStatus =
  | "requested"
  | "approved"
  | "rejected"
  | "processing"
  | "refunded"
  | "failed";

export interface AdminAttachmentOut {
  id: number;
  filename: string;
  content_type: string;
  size_bytes: number;
  created_at: string;
  download_url: string;
}

export interface AdminMessageOut {
  id: number;
  author_type: AuthorType;
  author_id: string | null;
  body: string;
  is_internal: boolean;
  created_at: string;
  attachments: AdminAttachmentOut[];
}

export interface AdminRefundRequestOut {
  id: number;
  ticket_id: number;
  subscription_id: number;
  stripe_invoice_id: string;
  stripe_charge_id: string | null;
  amount_requested_cents: number;
  currency: string;
  reason: string;
  status: RefundStatus;
  amount_approved_cents: number | null;
  stripe_refund_id: string | null;
  decided_by_admin_id: string | null;
  decided_at: string | null;
  decision_note: string | null;
  created_at: string;
}

export interface AdminTicketListItem {
  id: number;
  subject: string;
  category: TicketCategory;
  priority: TicketPriority;
  status: TicketStatus;
  requester_user_id: string;
  subscription_id: number;
  subscription_user_id: string | null;
  subscription_type: string | null;
  assigned_admin_id: string | null;
  satisfaction_rating: number | null;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
  has_refund: boolean;
}

export interface AdminTicketDetail extends AdminTicketListItem {
  satisfaction_comment: string | null;
  messages: AdminMessageOut[];
  attachments: AdminAttachmentOut[];
  refund_request: AdminRefundRequestOut | null;
}

export interface PaginatedAdminTickets {
  items: AdminTicketListItem[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface SupportStats {
  open: number;
  pending: number;
  on_hold: number;
  solved: number;
  closed: number;
  unassigned: number;
  refund_pending_count: number;
  refund_pending_amount_cents: number;
}

export interface BulkStatusResult {
  updated: number;
  failed: number;
}

export interface ListTicketsParams {
  status?: string;
  category?: string;
  priority?: string;
  assigned?: string;
  search?: string;
  page?: number;
  size?: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
async function safeError(res: Response | null | undefined): Promise<string | null> {
  if (!res) return null;
  try {
    const data = await res.json();
    return typeof data?.detail === "string" ? data.detail : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------
export async function listTickets(
  navigate: NavigateFunction,
  params?: ListTicketsParams
): Promise<PaginatedAdminTickets> {
  const q = new URLSearchParams();
  if (params?.status) q.set("status", params.status);
  if (params?.category) q.set("category", params.category);
  if (params?.priority) q.set("priority", params.priority);
  if (params?.assigned) q.set("assigned", params.assigned);
  if (params?.search) q.set("search", params.search);
  q.set("page", String(params?.page ?? 1));
  q.set("size", String(params?.size ?? 25));
  const res = await apiFetch(`${API_URL}/admin/support/tickets?${q.toString()}`, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement des tickets");
  return res.json() as Promise<PaginatedAdminTickets>;
}

export async function getStats(navigate: NavigateFunction): Promise<SupportStats> {
  const res = await apiFetch(`${API_URL}/admin/support/stats`, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement des statistiques");
  return res.json() as Promise<SupportStats>;
}

export async function getTicket(navigate: NavigateFunction, id: number): Promise<AdminTicketDetail> {
  const res = await apiFetch(`${API_URL}/admin/support/tickets/${id}`, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement du ticket");
  return res.json() as Promise<AdminTicketDetail>;
}

export async function postMessage(
  navigate: NavigateFunction,
  id: number,
  payload: { body: string; is_internal: boolean }
): Promise<AdminTicketDetail> {
  const res = await apiFetch(
    `${API_URL}/admin/support/tickets/${id}/messages`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    navigate
  );
  if (!res?.ok) {
    const msg = await safeError(res);
    throw new Error(msg || "Erreur lors de l'envoi du message");
  }
  return res.json() as Promise<AdminTicketDetail>;
}

export interface TicketPatch {
  status?: TicketStatus;
  priority?: TicketPriority;
  category?: TicketCategory;
  assigned_admin_id?: string | null;
}

export async function patchTicket(
  navigate: NavigateFunction,
  id: number,
  patch: TicketPatch
): Promise<AdminTicketDetail> {
  const res = await apiFetch(
    `${API_URL}/admin/support/tickets/${id}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    },
    navigate
  );
  if (!res?.ok) {
    const msg = await safeError(res);
    throw new Error(msg || "Erreur lors de la mise à jour du ticket");
  }
  return res.json() as Promise<AdminTicketDetail>;
}

export async function assignTicket(
  navigate: NavigateFunction,
  id: number,
  assigned_admin_id?: string | null
): Promise<AdminTicketDetail> {
  // Corps omis / null => auto-assignation côté backoffice.
  const res = await apiFetch(
    `${API_URL}/admin/support/tickets/${id}/assign`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ assigned_admin_id: assigned_admin_id ?? null }),
    },
    navigate
  );
  if (!res?.ok) {
    const msg = await safeError(res);
    throw new Error(msg || "Erreur lors de l'assignation");
  }
  return res.json() as Promise<AdminTicketDetail>;
}

export async function approveRefund(
  navigate: NavigateFunction,
  id: number,
  payload: { amount_approved_cents?: number; decision_note?: string }
): Promise<AdminRefundRequestOut> {
  const res = await apiFetch(
    `${API_URL}/admin/support/tickets/${id}/approve-refund`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    navigate
  );
  if (!res?.ok) {
    const msg = await safeError(res);
    throw new Error(msg || "Erreur lors de l'approbation du remboursement");
  }
  return res.json() as Promise<AdminRefundRequestOut>;
}

export async function rejectRefund(
  navigate: NavigateFunction,
  id: number,
  payload: { decision_note: string }
): Promise<AdminRefundRequestOut> {
  const res = await apiFetch(
    `${API_URL}/admin/support/tickets/${id}/reject-refund`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    navigate
  );
  if (!res?.ok) {
    const msg = await safeError(res);
    throw new Error(msg || "Erreur lors du refus du remboursement");
  }
  return res.json() as Promise<AdminRefundRequestOut>;
}

export async function bulkStatus(
  navigate: NavigateFunction,
  payload: { ticket_ids: number[]; status: TicketStatus }
): Promise<BulkStatusResult> {
  const res = await apiFetch(
    `${API_URL}/admin/support/tickets/bulk/status`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    navigate
  );
  if (!res?.ok) {
    const msg = await safeError(res);
    throw new Error(msg || "Erreur lors de la mise à jour groupée");
  }
  return res.json() as Promise<BulkStatusResult>;
}

// Télécharge une pièce jointe (binaire) et renvoie une object URL.
// L'appelant est responsable du URL.revokeObjectURL() après usage.
export async function downloadAttachment(
  navigate: NavigateFunction,
  id: number
): Promise<string> {
  const res = await apiFetch(
    `${API_URL}/admin/support/attachments/${id}/download`,
    { method: "GET" },
    navigate
  );
  if (!res?.ok) throw new Error("Erreur lors du téléchargement de la pièce jointe");
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
